import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { of, Observable } from 'rxjs';
import { Loadable, LoadableOf } from '@schenck/rotec-components';
import { startWith, delay, map, catchError } from 'rxjs/operators';
import { apiBaseUrl } from 'src/config';

export type NotificationSeverity = 'Critical' | 'Major' | 'Minor' | 'Warning';

export type NotificationType = 'event' | 'alarm';

export interface NotificationEntry {
  /**
   * Unique id of the notification.
   */
  id: string;
  /**
   * Severity of the notification.
   */
  severity: NotificationSeverity;
  /**
   * Type of notification.
   */
  notificationType: NotificationType;
  /**
   * A category of notification.
   */
  type: string;
  /**
   * Human readable text.
   */
  text: string;
  /**
   * When the notification occured.
   */
  timestamp: Date;
  /**
   * Whether that notification was acknowledged or not.
   */
  ack: boolean;
  /**
   * Whether that notification is active or not.
   */
  active: boolean;
  /**
   * The machine id the notification is related to.
   */
  realTwinId: string;
}

export interface NotificationStatistic {
  critical: number;
  major: number;
  minor: number;
  warning: number;
}

export interface NotificationsResponse {
  /**
   * For the last 7 days, info on the number of notifications and its criticality.
   */
  statistics: NotificationStatistic[];
  /**
   * The notifications.
   */
  data: NotificationEntry[];
}

const baseUrl = `${apiBaseUrl}/data/notifications`;

@Injectable()
export class NotificationsApi {
  private readonly http = inject(HttpClient);

  get$(fromDate?: Date): Observable<LoadableOf<NotificationsResponse>> {
    let url = `${baseUrl}?offset=${new Date().getTimezoneOffset()}`;
    if (fromDate) {
      url += `&dateFrom=${fromDate.toISOString()}`;
    }
    return this.http.get<any>(url).pipe(
      map((resp) => Loadable.done({ ...resp, data: resp.data.map(serializeDate) })),
      startWith(Loadable.loading())
    );
  }

  /**
   * Acknowledges given notification ids.
   */
  acknowledge$(ids: string[]): Observable<boolean> {
    // TODO integrate api
    // return of(true);
    return this.http.put(baseUrl, { ids }).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }
}

function serializeDate(content: any): NotificationsResponse {
  return {
    ...content,
    timestamp: new Date(content.timestamp),
  };
}
